import Decimal from "decimal.js";
import { batch, Component, createMemo, For, Show } from "solid-js";
import { z } from "zod";
import {
  classificationCategory,
  ClassificationType,
  classificationType,
  expensesType,
  isMeasurementUnit,
  measurementUnit,
  MeasurementUnitEnum,
  measurementUnitNames,
  vatCategory,
  vatExemption,
  vatExemptionDescriptions,
  vatRates,
  ZeroVAT,
} from "~/mydata";
import { isInvoiceGR, salesInvoiceSelfPricing } from "~/onesign/invoice";
import { invoiceClassifications } from "~/onesign/invoice-classifications";
import {
  itemDiscount,
  itemGrossValue,
  itemNetValueNoDiscount,
  itemTotalDeductedTaxAmount,
  itemTotalTaxAmount,
  itemVAT
} from "~/onesign/invoice-totals";
import { timestamp } from "~/schemas";
import { Good, InvoiceItemType, invoiceItemTypeNames, invoiceItemTypes, Service } from "~/schemas/invoice-item";
import { resolveInvoiceItemSchema, SupportedInvoiceType } from "~/schemas/invoices";
import { createZodForm } from "~/zod-forms";
import AddInvoiceItemTaxForm from "./AddInvoiceItemTaxForm";
import AmountFormControl from "./AmountFormControl";
import AmountInput from "./AmountInput";
import Button from "./Button";
import { useDebounce } from "./debounce";
import { maybeNativeEnum, replaceDotWithComma } from "./helpers";
import InvoiceItemClassificationSelect from "./InvoiceItemClassificationSelect";
import InvoiceItemTaxes from "./InvoiceItemTaxes";
import InvoiceItemVatClassificationSelect from "./InvoiceItemVatClassificationSelect";
import SelectFormControl from "./SelectFormControl";
import StringFormControl from "./StringFormControl";

const intoVATCategory = maybeNativeEnum(vatCategory);
const intovatExemption = maybeNativeEnum(vatExemption);

const Product = z.object({
  invoiceItemId: z.string().uuid(),
  issuerId: z.string().uuid(),
  classificationId: z.number().int(),
  description: z.string(),
  code: z.string(),
  productType: z.enum([Good, Service]).optional(),
  quantity: z.number().int(),
  measurementUnit: z.string(),
  netValuePerUnit: z.number(),
  vatId: z.number().int(),
  vatExemption: z.number().int(),
  otherTaxId: z.number().int(),
  feeId: z.number().int(),
  stampDutyId: z.number().int(),
  withheldId: z.number().int(),
  deductions: z.number(),
  invoiceType: z.string(),
  category: z.enum(classificationCategory),
  type: z.enum(classificationType),
  updatedAt: timestamp,
  createdAt: timestamp,
});

export type ProductType = z.infer<typeof Product>;

export const invoiceItemForm = (
  invoiceType: SupportedInvoiceType,
  itemType: InvoiceItemType
): Component<{
  item?: any;
  index?: number;
  products: ProductType[] | undefined;
  onSubmit(item: any, index?: number): void;
}> => {
  const schema = resolveInvoiceItemSchema(invoiceType, itemType);
  return (props) => {
    const [Form, form] = createZodForm({
      schema,
      initialInput: {
        vat: isInvoiceGR(invoiceType)
          ? { vatCategory: vatCategory.VATRate24 }
          : {
              vatCategory: vatCategory.ZeroVAT,
              vatExemptionCategory: vatExemption.Article14,
            },
        ...props.item,
        itemType,
      },
    });
    const [itemVATCategory, setItemVATCategory] = form.bind("vat.vatCategory");
    const [itemvatExemption, setItemvatExemption] = form.bind(
      "vat.vatExemptionCategory"
    );
    const [classification, setClassification] = form.bind("classification");
    const [quantity, setQuantity] = form.bind("quantity");
    const [unit, setUnit] = form.bind("measurementUnit");
    const [netValuePerUnit, setNetValuePerUnit] = form.bind("netValuePerUnit");
    const [itemCode, setItemCode] = form.bind("code");
    const [itemDescription, setItemDescription] = form.bind("description");
    const [itemTaxes, setItemTaxes] = form.bind("taxes");
    const [discount, setDiscount] = form.bind("discount");

    const discountAmount = createMemo(() => itemDiscount(form.input() as any));
    const netValue = createMemo(() => itemNetValueNoDiscount(form.input() as any));
    const vatAmount = createMemo(() => itemVAT(form.input() as any));
    const totalDeductedTax = createMemo(() =>
      itemTotalDeductedTaxAmount(form.input() as any)
    );
    const totalTaxValue = createMemo(() =>
      itemTotalTaxAmount(form.input() as any)
    );
    const isZeroVAT = () => itemVATCategory() === vatCategory.ZeroVAT;

    const classifications = invoiceClassifications[invoiceType];
    const products = props.products?.filter(item =>
      classifications[item.category]?.some((classification: ClassificationType) => classification == item.type)
    );
    const expensesVatClassifications = expensesType.filter((type) => type.startsWith("VAT_"));
    const [vatClassification, setVatClassification] = form.bind("vatClassification");
    return (
      <div>
        <Form
          handleSubmit={(_) => {
            props.onSubmit(form.input(), props.index);
          }}
        >
          <div class="row mb-2 gx-2">
            <div class="col">
              <SelectFormControl
                label="Είδη"
                classList={{
                  ["p-2"]: true,
                }}
                onChange={(e) => {
                  if (!e.currentTarget.value) {
                    setItemDescription("");
                    setNetValuePerUnit(0);
                    setQuantity(0);
                    setUnit(undefined);
                    setItemVATCategory(intoVATCategory(1));
                    setItemvatExemption(intovatExemption(undefined));
                    setItemCode("");
                    setClassification(undefined);
                    return;
                  }
                  const index = Number.parseInt(e.currentTarget.value);
                  const item = products![index];
                  const vatExemption = item.vatExemption;
                  let unitName = item.measurementUnit;
                  unitName =
                    unitName!.charAt(0).toUpperCase() + unitName!.slice(1);
                  if (isMeasurementUnit(measurementUnit[unitName as MeasurementUnitEnum])) {
                    const unit = measurementUnit[unitName as MeasurementUnitEnum];
                    setUnit(unit);
                  }
                  setItemDescription(item!.description);
                  setNetValuePerUnit(item!.netValuePerUnit);
                  setQuantity(item!.quantity);
                  setItemVATCategory(intoVATCategory(item?.vatId));
                  setItemCode(item!.code);
                  setClassification({
                    type: item!.type,
                    category: item!.category,
                  });
                  if (vatExemption) {
                    setItemvatExemption(intovatExemption(vatExemption));
                  } else {
                    setItemvatExemption(intovatExemption(undefined));
                  }
                }}
              >
                <option value="">Επιλέξτε...</option>
                <For each={products}>
                  {(item, index) => {
                    return <option value={index()}>{item.description}</option>;
                  }}
                </For>
              </SelectFormControl>
            </div>
          </div>
          <div class="row mb-2 gx-2">
            <div class="col-4 me-3">
              <SelectFormControl
                value={itemType}
                label="Είδος Γραμμής"
                classList={{
                  ["p-2"]: true,
                }}
              >
                <For each={invoiceItemTypes}>
                  {(typ) => (
                    // Temporarily hide item types except line_item
                    typ == "line_item"
                      ? <option value={typ}>{invoiceItemTypeNames[typ]}</option>
                      : undefined
                  )}
                </For>
              </SelectFormControl>
            </div>
            <div class="col">
              <InvoiceItemClassificationSelect
                itemClassification={classification()}
                setItemClassification={setClassification}
                errors={classification.dirty && classification.issues}
                invoiceType={invoiceType}
                classList={{
                  ["p-2"]: true,
                }}
              />
            </div>
            <Show
              when={invoiceType === salesInvoiceSelfPricing}>
              <div class="col ms-3">
                <InvoiceItemVatClassificationSelect
                  itemVatClassifications={expensesVatClassifications}
                  itemVatClassification={vatClassification()}
                  setItemVatClassification={setVatClassification}
                  errors={
                    vatClassification.dirty &&
                    vatClassification.issues
                  }
                  classList={{
                    ["p-2"]: true
                  }}
                />
              </div>
            </Show>
          </div>
          <div class="row mb-2 gx-2">
            <div class="col-md-4 me-3">
              <StringFormControl
                label="Κωδικός"
                value={itemCode()}
                onChange={(e) => setItemCode(e.currentTarget.value)}
                classList={{
                  ["p-2"]: true,
                }}
              />
            </div>
            <div class="col">
              <StringFormControl
                label="Περιγραφή"
                value={itemDescription()}
                errors={itemDescription.dirty && itemDescription.issues}
                onInput={(e) =>
                  useDebounce(setItemDescription)(e.currentTarget.value)
                }
                classList={{
                  ["p-2"]: true,
                }}
              />
            </div>
          </div>
          <div class="row gx-2 justify-content-end mb-2">
            <div class="col-md-2 me-3 no-margin-right">
              <SelectFormControl
                label="Φ.Π.Α."
                value={itemVATCategory()}
                errors={itemVATCategory.dirty && itemVATCategory.issues}
                disabled={!isInvoiceGR(invoiceType)}
                onChange={(e) =>
                  setItemVATCategory(intoVATCategory(e.currentTarget.value))
                }
                classList={{
                  ["p-2"]: true,
                }}
              >
                <Show
                  when={isInvoiceGR(invoiceType)}
                  fallback={<option value={ZeroVAT}>Φ.Π.Α. 0%</option>}
                >
                  <For each={Object.entries(vatRates)}>
                    {([id, rate]) => <option value={id}>{rate}%</option>}
                  </For>
                </Show>
              </SelectFormControl>
            </div>
            <div class="col me-3">
              <AmountFormControl
                signal={[netValuePerUnit, setNetValuePerUnit]}
                errors={netValuePerUnit.dirty && netValuePerUnit.issues}
                debounce={200}
                label="Τιμή μονάδας (€)"
                classList={{
                  ["p-2"]: true,
                }}
              />
            </div>
            <div class="col me-3">
              <AmountFormControl
                signal={[quantity, setQuantity]}
                errors={quantity.dirty && quantity.issues}
                decimalPlaces={quantityDecimalPlaces(unit())}
                debounce={700}
                label="Ποσότητα"
                classList={{
                  ["p-2"]: true,
                }}
              />
            </div>
            <div class="col me-3">
              <SelectFormControl
                label="Μονάδες"
                value={unit() ?? ""}
                errors={unit.dirty && unit.issues}
                onChange={(e) => {
                  const unit = maybeNativeEnum(measurementUnit)(
                    e.currentTarget.value
                  );
                  const d = quantityDecimalPlaces(unit);
                  batch(() => {
                    setUnit(unit);
                    setQuantity((q) =>
                      q === undefined ? undefined : new Decimal(q).toFixed(d)
                    );
                  });
                }}
                classList={{
                  ["p-2"]: true,
                }}
              >
                <option value="" selected={unit() === undefined}>
                  --
                </option>
                <For each={Object.values(measurementUnit)}>
                  {(unitId) => (
                    <option value={unitId}>
                      {measurementUnitNames[unitId]}
                    </option>
                  )}
                </For>
              </SelectFormControl>
            </div>
            <div class="col me-3">
              <AmountFormControl
                signal={[discount, setDiscount]}
                errors={discount.dirty && discount.issues}
                debounce={200}
                label="Έκπτωση (€)"
                classList={{
                  ["p-2"]: true,
                }}
              />
            </div>
            <div class="col-md-3">
              <AmountFormControl
                label="Καθαρή αξία (€)"
                disabled
                value={
                  replaceDotWithComma(
                    netValue()?.toFixed(2) ?? ""
                  )
                }
                classList={{
                  ["p-2"]: true,
                }}
              />
            </div>
          </div>
          <div class="row gx-2 justify-content-end mb-2">
            <div
              class="col me-3 no-margin-right"
              classList={{
                "text-muted": !isZeroVAT(),
              }}
            >
              <SelectFormControl
                label="Κατηγορία εξαίρεσης Φ.Π.Α."
                disabled={!isZeroVAT()}
                errors={itemvatExemption.dirty && itemvatExemption.issues}
                classList={{
                  "text-muted": !isZeroVAT(),
                  ["p-2"]: true,
                }}
                value={itemvatExemption()}
                onChange={(e) =>
                  setItemvatExemption(intovatExemption(e.currentTarget.value))
                }
              >
                <option value="">
                  {isZeroVAT() ? "Επιλέξτε..." : "Συντελεστής Φ.Π.Α. > 0%"}
                </option>
                <For each={Object.values(vatExemption)}>
                  {(id) => (
                    <option value={id}>{vatExemptionDescriptions[id]}</option>
                  )}
                </For>
              </SelectFormControl>
            </div>
            <div class="col-md-3">
              <AmountFormControl
                label={<span>Αξία Φ.Π.Α (€)</span>}
                disabled
                value={
                  replaceDotWithComma(
                    vatAmount()?.toFixed(2) ?? "--"
                  )
                }
                classList={{
                  ["p-2"]: true,
                }}
              />
            </div>
          </div>
          <div class="row gx-2 mb-2">
            <div class="col" />
            <div class="col-md-3">
              <label class="form-label">Κρατήσεις / Φόροι (€)</label>
              <div class="input-group">
                <AmountInput
                  class="form-control text-end p-2"
                  disabled
                  value={
                    replaceDotWithComma(
                      totalDeductedTax().toFixed(2)
                    )
                  }
                />
                <AmountInput
                  class="form-control text-end p-2"
                  disabled
                  value={
                    replaceDotWithComma(
                      totalTaxValue().toFixed(2)
                    )
                  }
                />
              </div>
            </div>
          </div>
          <div class="row gx-2 mb-2">
            <div class="col" />
            <div class="col-md-2 me-3">
              <label class="form-label">&nbsp;</label>

              <Button
                class="form-control"
                kind="primary"
                disabled={
                  form.submitting() ||
                  (form.lastSubmit()?.success === false && !form.valid())
                }
              >
                Αποθήκευση
              </Button>
            </div>
            <div class="col-md-3">
              <AmountFormControl
                label="Συνολική αξία (€)"
                disabled
                value={
                  replaceDotWithComma(
                    itemGrossValue(form.input() as any)?.toFixed(2) ?? ""
                  )
                }
                classList={{
                  ["p-2"]: true,
                }}
              />
            </div>
          </div>
        </Form>
        <Show when={isInvoiceGR(invoiceType)}>
          <AddInvoiceItemTaxForm
            item={form.input() as any}
            addTaxItem={(tax) =>
              setItemTaxes((taxes) => (taxes ? [...taxes, tax] : [tax]))
            }
          ></AddInvoiceItemTaxForm>
          <InvoiceItemTaxes
            taxes={itemTaxes() ?? []}
            item={form.input() as any}
          ></InvoiceItemTaxes>
        </Show>

        {/* <pre>{JSON.stringify(form.input, undefined, 2)}</pre> */}
      </div>
    );
  };
};

export default invoiceItemForm;

const quantityDecimalPlaces = (input: unknown) =>
  isMeasurementUnit(input) &&
  (input === measurementUnit.Liters || input === measurementUnit.Kilograms)
    ? 2
    : 0;
